import * as Sentry from "@sentry/react";

import { createLocalAxios } from "../../lib/api.js";
import apiMessages from "../../lib/apiMessages.js";

const { VITE_MALFUNCTIONS_API_KEY } = import.meta.env;

import {
  loadingMalfunctions,
  loadingMalfunctionsCategories,
  loadingMalfunctionsCategoriesDone,
  loadingMalfunctionsDone,
  setMalfunctions,
  setMalfunctionsCategories,
} from "./slice.js";

export const fetchMalfunctions =
  ({ vehicleNumber }) =>
  async (dispatch, getState) => {
    dispatch(loadingMalfunctions());
    const localAxios = createLocalAxios(getState);
    const url = `malfunctions/${vehicleNumber}/open`;

    let response;
    try {
      response = await localAxios.get(url);
    } catch (error) {
      console.error("Error while fetching the malfuntions:", error);
      throw error;
    } finally {
      await dispatch(loadingMalfunctionsDone());
    }

    await dispatch(setMalfunctions(response.data.rows));

    return response.data.rows;
  };

export const fetchMalfunctionsCategories = () => async (dispatch, getState) => {
  dispatch(loadingMalfunctionsCategories());
  const localAxios = createLocalAxios(getState);
  const url = `malfunctions/categories`;

  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the malfuntions categories:", error);
    throw error;
  } finally {
    await dispatch(loadingMalfunctionsCategoriesDone());
  }

  await dispatch(setMalfunctionsCategories(response.data));

  return response.data;
};

export const createMalfunctionCategory =
  (body) => async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);
    const url = "malfunctions/work-order";

    let response;
    try {
      response = await localAxios.post(url, body);
    } catch (error) {
      console.error("Error while creating the malfuntion category:", error);
      Sentry.captureException(
        new Error(
          `Something went wrong: softwareNotificationForm post/patch-> ${error}`,
        ),
      );
      throw error;
    }

    if (response.data.message !== apiMessages.MSG_SUCCESS) {
      throw new Error(response.data.message);
    }

    return response;
  };

export const saveMalfunctionCategory =
  (body, categoryId) => async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);
    const url = categoryId
      ? `malfunctions/categories/${categoryId}`
      : "malfunctions/categories/";

    let response;
    try {
      if (categoryId) {
        response = await localAxios.patch(url, body);
      } else {
        response = await localAxios.post(url, body);
      }
    } catch (error) {
      console.error("Error while saving the malfunction category:", error);
      Sentry.captureException(
        new Error(
          `Something went wrong: saveMalfunctionCategory post/patch-> ${error}`,
        ),
      );
      throw error;
    }

    if (response.data.message !== apiMessages.MSG_SUCCESS) {
      throw new Error(response.data.message);
    }

    return response;
  };

export const removeMalfunctionCategory = (id) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `malfunctions/categories/${id}`;

  try {
    await localAxios.delete(url);
  } catch (error) {
    console.error(`Error deleting malfunction category: ${error}`);
    Sentry.captureException(
      new Error(`Something went wrong: removeMalfunctionCategory -> ${error}`),
    );
  }
};

export const fetchMalfunctionCategoryById =
  (id) => async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);
    const url = `malfunctions/categories/${id}`;

    let response;
    try {
      response = await localAxios.get(url);
    } catch (error) {
      console.error(
        "Error while fetching the malfunction category by id:",
        error,
      );
      new Error(
        `Something went wrong: fetchMalfunctionCategoryById get -> ${error}`,
      );
    }
    dispatch(setMalfunctionsCategories([response.data]));

    return response.data;
  };

export const fetchPublicMalfunctionsCategories =
  () => async (dispatch, getState) => {
    dispatch(loadingMalfunctionsCategories());
    const localAxios = createLocalAxios(getState);
    const url = "malfunctions/categories/public/open";
    const body = { key: VITE_MALFUNCTIONS_API_KEY };

    let response;
    try {
      response = await localAxios.post(url, body);
    } catch (error) {
      console.error(
        "Error while fetching the public malfuntions categories:",
        error,
      );
      throw error;
    } finally {
      await dispatch(loadingMalfunctionsCategoriesDone());
    }

    if (response.data.error) {
      throw new Error(response.data.error);
    }

    await dispatch(setMalfunctionsCategories(response.data));

    return response.data;
  };

export const fetchPublicOpenMalfunctions =
  ({ id, internal }) =>
  async (dispatch, getState) => {
    dispatch(loadingMalfunctions());

    const localAxios = createLocalAxios(getState, { internal });
    const url = internal
      ? `malfunctions/internal/${id}/open`
      : `malfunctions/public/${id}/open`;
    const body = { key: VITE_MALFUNCTIONS_API_KEY };

    let response;
    try {
      response = await localAxios.post(url, body);
    } catch (error) {
      console.error("Error while fetching the public open malfuntions:", error);
      throw error;
    } finally {
      await dispatch(loadingMalfunctionsDone());
    }

    if (response.data.error) {
      throw new Error(response.data.error);
    }
    await dispatch(setMalfunctions(response.data));

    return response.data.error;
  };
